import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { AppState } from '../../../../../state';
import {
  addStudyResearcher,
  getStudyResearcher,
  setSingleStudyResearcherPageVisibility,
  updateStudyResearcher,
} from '../../../../state/study-researcher/study-researcher.actions';
import {
  selectNewStudyResearcher,
  selectPrivilegesOpened,
  selectSelectedStudyResearcher,
} from '../../../../state/study-researcher/study-researcher.selectors';
import { selectUser } from '../../../../../state/user/user.selectors';
import { User } from '../../../../../models/user';
import { Study } from '../../../../../models/study';
import { StudyType } from '../../../../../models/study-type';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../../core/dashboard-constants';
import { EMAIL_PATTERN, removeWhitespaces } from '../../../../../core/custom-form-validators';

export const TABLE_SECTION_LABELS = [
  'Study Details',
  'Interventions',
  'Modules',
  'Researchers',
  'Participants',
  'Metrics',
];
export const TABLE_ACTION_LABELS = ['Not Visible', 'Visible', 'Visible - Create, Edit, Delete'];

@Component({
  selector: 'dashboard-study-researcher',
  templateUrl: './study-researcher.component.html',
  styleUrls: ['./study-researcher.component.scss'],
})
export class StudyResearcherComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() selectedStudy: Study;
  @Input() isAddResearcher: boolean;

  researcherForm: FormGroup;

  privilegesOpened$: Observable<boolean>;

  newStudyResearcher: User;
  selectedStudyResearcher: User;
  currentUser: User;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  privilegeSections = Object.values(PRIVILEGE_SECTIONS);
  privilegeActions = Object.values(PRIVILEGE_ACTIONS);

  tableSectionLabels = TABLE_SECTION_LABELS;
  tableActionLabels = TABLE_ACTION_LABELS;

  StudyType = StudyType;

  nameChange = false;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    public privilegeService: PrivilegeService
  ) {}

  ngOnInit(): void {
    this.researcherForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(EMAIL_PATTERN), removeWhitespaces]],
      firstname: ['', [Validators.required, Validators.maxLength(255), removeWhitespaces]],
      lastname: ['', [Validators.required, Validators.maxLength(255), removeWhitespaces]],
      privileges: this.formBuilder.array([]),
    });

    this.initPrivilegesFormArray();

    this.privilegesOpened$ = this.store.select(selectPrivilegesOpened);

    this.subscriptions.add(
      this.store.select(selectNewStudyResearcher).subscribe((newStudyResearcher) => {
        this.newStudyResearcher = newStudyResearcher;
        if (this.newStudyResearcher?.superuser) {
          this.disablePrivileges();
        }
      })
    );
    this.subscriptions.add(
      this.store.select(selectSelectedStudyResearcher).subscribe((selectedStudyResearcher) => {
        this.selectedStudyResearcher = selectedStudyResearcher;

        if (this.selectedStudyResearcher?.superuser) {
          this.disablePrivileges();
        }
        if (
          this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.RESEARCHERS, PRIVILEGE_ACTIONS.VIEW) &&
          !this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.RESEARCHERS, PRIVILEGE_ACTIONS.ALL)
        ) {
          this.researcherForm.disable();
        }
      })
    );
    this.subscriptions.add(this.store.select(selectUser).subscribe((currentUser) => (this.currentUser = currentUser)));

    this.isAddResearcher ? this.email.enable() : this.email.disable();
  }

  ngAfterViewInit() {
    if (this.newStudyResearcher?.superuser || this.selectedStudyResearcher?.superuser) {
      this.setPrivilegesForAdmin();
    }
  }

  get email(): AbstractControl {
    return this.researcherForm.get('email');
  }

  get firstName(): AbstractControl {
    return this.researcherForm.get('firstname');
  }

  get lastName(): AbstractControl {
    return this.researcherForm.get('lastname');
  }

  get privileges(): FormArray {
    return this.researcherForm.get('privileges') as FormArray;
  }

  initPrivilegesFormArray(): void {
    this.privilegeSections.forEach((privilegeSection) => {
      this.privileges.push(
        this.formBuilder.group({
          id: [this.selectedStudyResearcher ? this.selectedStudyResearcher.id : 0],
          studyId: [this.selectedStudy?.id],
          section: [privilegeSection],
          action: [this.privilegeActions[this.privilegeActions.length - 1]],
        })
      );
    });
  }

  disablePrivileges(): void {
    this.privileges.disable();
    this.toastrService.info('This researcher is admin, therefore his/her privileges cannot be changed.');
  }

  setPrivilegesForAdmin(): void {
    this.privileges.controls.forEach((privilegeControl) =>
      privilegeControl.patchValue({ action: PRIVILEGE_ACTIONS.ALL })
    );
  }

  backToResearchers() {
    this.store.dispatch(setSingleStudyResearcherPageVisibility({ singleResearcherVisible: false }));
  }

  checkEmail(): void {
    if (this.email.invalid) {
      return;
    }
    this.store.dispatch(getStudyResearcher({ email: this.email.value }));
    this.email.disable();
  }

  saveStudyResearcher(): void {
    if (this.researcherForm.invalid) {
      return;
    }

    let researcherFormValue = this.researcherForm.getRawValue();
    researcherFormValue = {
      ...researcherFormValue,
      parentId: this.newStudyResearcher?.createStudies ? null : this.currentUser?.id,
      isEditing: !this.isAddResearcher,
    };

    researcherFormValue.privileges = researcherFormValue?.privileges?.filter(
      (privilege) => privilege.action !== PRIVILEGE_ACTIONS.HIDE
    );

    if (this.isAddResearcher && !this.newStudyResearcher) {
      this.store.dispatch(
        addStudyResearcher({
          studyId: this.selectedStudy?.id,
          researcher: researcherFormValue,
        })
      );
      return;
    }

    this.nameChange = false;

    this.store.dispatch(
      updateStudyResearcher({
        studyId: this.selectedStudy?.id,
        researcher: {
          ...researcherFormValue,
          id: this.newStudyResearcher?.id || this.selectedStudyResearcher?.id,
        },
        addExisting: !!this.newStudyResearcher,
      })
    );
  }

  credentialsOnChange(event): void {
    const oldFirstname = this.selectedStudyResearcher.firstname;
    const oldLastname = this.selectedStudyResearcher.lastname;

    const newFirstname = this.researcherForm.get('firstname').value;
    const newLastname = this.researcherForm.get('lastname').value;

    this.nameChange = oldFirstname !== newFirstname || oldLastname !== newLastname;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
